import { useCallback, useRef, useState } from 'react';
import { confirm, presign, uploadToS3 } from './fileUploadClient';
import type { MediaCategory, PresignedItem } from './types';

/**
 * 필드가 큐에 보고하는 한 칸(slot). 기존에 저장된 URL 이거나, 아직 업로드 전인
 * 로컬 File 둘 중 하나다. flush 시 File slot 은 publicUrl 로 치환된다.
 */
export type UploadSlot =
  | { kind: 'existing'; url: string }
  | { kind: 'pending'; file: File };

interface FieldRegistration {
  category: MediaCategory;
  multiple: boolean;
  getSlots: () => ReadonlyArray<UploadSlot>;
}

/** flush() 가 등록된 name 별로 돌려주는 최종값. 단일=문자열|null, 다중=문자열[]. */
export type ResolvedValues = Record<string, string | string[] | null>;

export interface ImageUploadQueue {
  register: (name: string, registration: FieldRegistration) => void;
  unregister: (name: string) => void;
  flush: () => Promise<ResolvedValues>;
  isUploading: boolean;
  hasPending: boolean;
  error: string | null;
}

const collectPendingFiles = (
  fields: ReadonlyMap<string, FieldRegistration>,
): { file: File; category: MediaCategory }[] =>
  Array.from(fields.values()).flatMap((field) =>
    field
      .getSlots()
      .filter((slot): slot is Extract<UploadSlot, { kind: 'pending' }> => slot.kind === 'pending')
      .map((slot) => ({ file: slot.file, category: field.category })),
  );

const resolveField = (
  field: FieldRegistration,
  uploaded: ReadonlyMap<File, string>,
): string | string[] | null => {
  const urls = field
    .getSlots()
    .map((slot) => (slot.kind === 'existing' ? slot.url : uploaded.get(slot.file)))
    .filter((url): url is string => Boolean(url));

  if (field.multiple) return urls;
  return urls[0] ?? null;
};

/**
 * 폼당 1개 생성하는 업로드 coordinator 훅. "저장 시 업로드" 모델:
 * 필드는 선택 즉시 로컬 미리보기만 보여주고 pending 등록하며, 실제 S3 업로드는
 * flush() 시점에 전체 pending 을 배치 presign → 병렬 PUT → 배치 confirm 으로 처리한다.
 */
export const useImageUploadQueue = (): ImageUploadQueue => {
  const fieldsRef = useRef<Map<string, FieldRegistration>>(new Map());
  const [isUploading, setIsUploading] = useState(false);
  const [hasPending, setHasPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshPending = useCallback(() => {
    setHasPending(collectPendingFiles(fieldsRef.current).length > 0);
  }, []);

  const register = useCallback(
    (name: string, registration: FieldRegistration) => {
      fieldsRef.current.set(name, registration);
      refreshPending();
    },
    [refreshPending],
  );

  const unregister = useCallback(
    (name: string) => {
      fieldsRef.current.delete(name);
      refreshPending();
    },
    [refreshPending],
  );

  const flush = useCallback(async (): Promise<ResolvedValues> => {
    const fields = fieldsRef.current;
    const pending = collectPendingFiles(fields);

    const buildResult = (uploaded: ReadonlyMap<File, string>): ResolvedValues =>
      Object.fromEntries(
        Array.from(fields.entries()).map(([name, field]) => [
          name,
          resolveField(field, uploaded),
        ]),
      );

    if (pending.length === 0) {
      return buildResult(new Map());
    }

    setIsUploading(true);
    setError(null);
    try {
      const presigned: PresignedItem[] = await presign(
        pending.map(({ file, category }) => ({ file, category })),
      );
      const pairs = pending.map(({ file }, index) => {
        const item = presigned[index];
        if (!item) throw new Error('업로드 URL 발급 응답이 올바르지 않습니다.');
        return { file, item };
      });
      await uploadToS3(pairs);

      const confirmed = await confirm(pairs.map(({ item }) => item.fileId));
      const uploaded = new Map<File, string>(
        pairs.map(({ file, item }) => [
          file,
          confirmed.get(item.fileId) ?? item.publicUrl,
        ]),
      );

      const result = buildResult(uploaded);
      refreshPending();
      return result;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : '이미지 업로드 중 오류가 발생했습니다.';
      setError(message);
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setIsUploading(false);
    }
  }, [refreshPending]);

  return { register, unregister, flush, isUploading, hasPending, error };
};
